import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ChevronLeft, ChevronRight } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default function EventCarousel({ events = [], interval = 7000 }) {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const list = events.length ? events : [];

  useEffect(() => {
    if (list.length <= 1) return;
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % list.length);
    }, interval);
    return () => clearInterval(timerRef.current);
  }, [list.length, interval]);

  if (!list.length) return null;
  const ev = list[idx];

  const go = (d) => {
    setIdx((i) => (i + d + list.length) % list.length);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setIdx((i) => (i + 1) % list.length);
      }, interval);
    }
  };

  return (
    <div
      data-testid="event-carousel"
      className="relative rounded-2xl overflow-hidden bg-secondary-dark text-white min-h-[460px] md:min-h-[520px]"
    >
      {list.map((e, i) => (
        <div
          key={e.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === idx ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          {e.image_url && (
            <img
              src={e.image_url}
              alt={e.title}
              className="absolute inset-0 w-full h-full object-cover opacity-55"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/10" />
        </div>
      ))}

      <Link
        to="/eventos"
        className="relative h-full min-h-[460px] md:min-h-[520px] p-8 md:p-12 flex flex-col justify-end z-10 group"
      >
        <span className="self-start inline-flex items-center px-3 py-1 rounded-full bg-[color:var(--inv-primary)] text-white text-[11px] uppercase tracking-widest font-semibold mb-4">
          Evento destacado
        </span>
        <h3 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05] max-w-3xl">
          {ev.title}
        </h3>
        <p className="mt-3 text-white/85 max-w-2xl line-clamp-3">{ev.description}</p>
        <div className="mt-5 flex flex-wrap gap-5 text-sm text-white/85">
          <span className="inline-flex items-center gap-1.5">
            <Calendar size={15} /> {formatDate(ev.event_date)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={15} /> {ev.location}
          </span>
        </div>
      </Link>

      {list.length > 1 && (
        <>
          <button
            data-testid="carousel-prev"
            onClick={(e) => {
              e.preventDefault();
              go(-1);
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center transition-colors"
            aria-label="Anterior"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            data-testid="carousel-next"
            onClick={(e) => {
              e.preventDefault();
              go(1);
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center transition-colors"
            aria-label="Siguiente"
          >
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-4 right-4 z-20 flex gap-1.5">
            {list.map((_, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.preventDefault();
                  setIdx(i);
                }}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-8 bg-white" : "w-2.5 bg-white/40"
                }`}
                aria-label={`Diapositiva ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
